//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

//  emit WithdrawRequested(owner, assets, shares);
import * as yup from 'yup'
import { InferType } from 'yup'
import { yupEthAddrRequired } from './Enums'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export interface CollateralCTO {
  collateralType: string
  collateralId: string
}
export const CollateralSchema = yup.object<CollateralCTO>().shape({
  collateralType: yup
    .string()
    .required()
    .default(' ')
    .label('Collateral Type')
    .meta(toFieldMetaData({ propName: 'collateralType' })),
  collateralId: yup
    .string()
    .required()
    .default(' ')
    .label('Collateral Id')
    .meta(toFieldMetaData({ propName: 'collateralId' })),
})

export interface CollateralCTOTypeCheck extends CollateralCTO, InferType<typeof CollateralSchema> {}

export const PoolOffchainDataSchemaValues = {
  poolAddrOffchain: yupEthAddrRequired
    .required()
    .label('Pool Address')
    .meta(toFieldMetaData({ propName: 'poolAddrOffchain' })),

  timestampOffchain: yup
    .number()
    .required()
    .default(Math.floor(Date.now()))
    .label('Offchain Data Timestamp')
    .meta(toFieldMetaData({ propName: 'timestampOffchain', stringType: StringTypeEnum.UNIX_DATE })),

  collateral: yup
    .array<CollateralCTO>(CollateralSchema)
    .required()
    .default([] as CollateralCTO[])
    .label('Collateral')
    .meta(toFieldMetaData({ propName: 'collateral' })),

  benchmarkRate: yup
    .string()
    .required()
    .label('Benchmark Rate')
    .default(' ')
    .meta({
      defaultDisplayValue: 'N/A',
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 20,
      ...toFieldMetaData({ propName: 'benchmarkRate' }),
    }),

  transferInDays: yup
    .number()
    .required()
    .label('Advance Processing Time (Days)')
    .default(0)
    .meta({
      defaultDisplayValue: '0',
      ...toFieldMetaData({
        propName: 'transferInDays',
        description: 'The number of business days that will lapse before a loan starts accruing interest. ',
      }),
    }),
  transferOutDays: yup
    .number()
    .required()
    .default(0)
    .label('Withdrawal Processing Time (Days)')
    .meta({
      defaultDisplayValue: '0',
      ...toFieldMetaData({
        propName: 'transferOutDays',
        description: 'The number of business days that will lapse before a loan is due to be repaid. ',
      }),
    }),

  advisoryFeeRate: yup
    .number()
    .required()
    .label('Advisory Fee Rate')
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'advisoryFeeRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 21,
        stringType: StringTypeEnum.OFFCHAIN_RATE,
      })
    ),
  platformFeeRate: yup
    .number()
    .required()
    .label('Platform Fee Rate')
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'platformFeeRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 22,
        stringType: StringTypeEnum.OFFCHAIN_RATE,
      })
    ),
  liquidityFeeRate: yup
    .number()
    .required()
    .label('Liquidity Fee Rate')
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'liquidityFeeRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 23,
        stringType: StringTypeEnum.OFFCHAIN_RATE,
      })
    ),
  contractualCurrency: yup
    .string()
    .required()
    .label('Contractual Currency')
    .default(' ')
    .meta({
      defaultDisplayValue: 'N/A',
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 20,
      ...toFieldMetaData({ propName: 'contractualCurrency' }),
    }),
  minimumTransferAmount: yup
    .number()
    .required()
    .label('Minimum Transfer Amount')
    .default(0)
    .meta(toFieldMetaData({ propName: 'minimumTransferAmount' })),
  maximumTransferAmount: yup
    .number()
    .required()
    .label('Maximum Transfer Amount')
    .default(0)
    .meta(toFieldMetaData({ propName: 'maximumTransferAmount' })),
  earlyWithdrawalProcessingPeriod: yup
    .number()
    .required()
    .label('Early Withdrawal Processing Period')
    .default(0)
    .meta(
      toFieldMetaData({ propName: 'earlyWithdrawalProcessingPeriod', stringType: StringTypeEnum.TIME_INTERVAL_DAYS })
    ),
  automaticRollover: yup
    .boolean()
    .required()
    .label('Automatic Rollover')
    .default(false)
    .meta(toFieldMetaData({ propName: 'automaticRollover' })),
  rolloverCollateral: yup
    .string()
    .required()
    .label('Rollover Collateral')
    .default(' ')
    .meta(toFieldMetaData({ propName: 'rolloverCollateral' })),
}

export const PoolOffchainDataSchema = yup.object(PoolOffchainDataSchemaValues)
export function PoolOffchainDataReportingSchema(properties?: string[]): yup.ObjectSchema<any> {
  if (!properties) {
    return PoolOffchainDataSchema
  } else {
    return PoolOffchainDataSchema.pick(properties as any[])
  }
}

// export function PoolOffchainDataLenderReportingSchema(properties?: string[]): yup.ObjectSchema<any> {
//   if (!properties) {
//     return PoolOffchainLenderDataSchema
//   } else {
//     const schemaValues: Record<string, yup.Schema> = {}
//     properties.forEach((prop) => {
//       if (PoolOffchainLenderDataSchemaValues[prop]) {
//         schemaValues[prop] = PoolOffchainLenderDataSchemaValues[prop]
//       }

//       if (!schemaValues[prop]) {
//         throw new Error(`Property ${prop} not found in PoolReportingAccountCTOSchema`)
//       }
//     })

//     return yup.object(schemaValues)
//   }
// }

export interface PoolOffchainDataCTO {
  poolAddrOffchain: EthAddr
  timestampOffchain: number
  collateral: CollateralCTO[]
  benchmarkRate: string
  transferInDays: number
  transferOutDays: number
  advisoryFeeRate: number
  platformFeeRate: number
  liquidityFeeRate: number
  contractualCurrency: string
  minimumTransferAmount: number
  maximumTransferAmount: number
  earlyWithdrawalProcessingPeriod: number
  automaticRollover: boolean
  rolloverCollateral: string
}

type PoolOffchainDataCTOInfer = InferType<typeof PoolOffchainDataSchema>

export const _testAPoolBaseOverview: PoolOffchainDataCTO = {} as unknown as PoolOffchainDataCTOInfer
export const _testBPoolBaseOverview: PoolOffchainDataCTOInfer = {} as unknown as PoolOffchainDataCTO
